import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClient } from '../api/client';
import { useTranslation } from '../i18n';
import { TelegramFileTypeSettings } from '../components/common/TelegramFileTypeSettings';
import { LanguageSettings } from '../components/common/LanguageSettings';
import { UserConfig } from '../types';

export function SettingsPage() {
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch user config on component mount
  useEffect(() => {
    const fetchUserConfig = async () => {
      try {
        const config = await ApiClient.getUserConfig();
        setUserConfig(config);
      } catch (error) {
        console.error('Failed to fetch user config:', error);
        // Set default config on error
        setUserConfig({
          onboarding_completed: true,
          onboarding_completed_at: null,
          telegram_file_type: 'document'
        });
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchUserConfig();
  }, []);

  const handleConfigUpdate = (newConfig: UserConfig) => {
    setUserConfig(newConfig);
  };

  const handleBack = () => {
    navigate('/profile');
  };

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-theme-hint hover:text-theme-text transition-colors"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            <span>{t('settings.backToProfile')}</span>
          </button>
          <h1 className="text-xl font-semibold text-theme-text">{t('settings.title')}</h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-w-2xl mx-auto">
        {isLoadingConfig ? (
          <div className="flex items-center gap-3 py-8 justify-center">
            <div className="w-6 h-6 border-2 border-theme-hint border-t-transparent rounded-full animate-spin"></div>
            <span className="text-theme-hint">{t('settings.loadingSettings')}</span>
          </div>
        ) : (
          <div className="space-y-6">
              {/* Language Settings Section */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="p-6">
                  <LanguageSettings />
                </div>
              </div>

              {/* Telegram Settings Section */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">send</span>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-theme-text">{t('settings.telegram.title')}</h2>
                      <p className="text-sm text-theme-hint">{t('settings.telegram.description')}</p>
                    </div>
                  </div>

                  <TelegramFileTypeSettings
                    userConfig={userConfig}
                    onConfigUpdate={handleConfigUpdate}
                  />
                </div>
              </div>

            {/* Future settings sections can be added here */}
            {/* 
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-theme-text mb-4">Other Settings</h2>
                // Future settings here
              </div>
            </div>
            */}
          </div>
        )}
      </div>
    </div>
  );
} 